import React, {useCallback, useEffect, useMemo, useState} from "react";
import Api from "../../../../../core/services/api/api";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import ToggleButtons from "../../../../components/base/button/toggle_buttons";
import BarChart from "../../../../components/base/chart/bar-chart";
import Utils from "../../../../../core/services/utils/utils";
import Paper from "@mui/material/Paper";
import Form from "../../../../components/base/form";
import Button from "../../../../components/base/button/button";
import {useTheme} from "@mui/material";
import {useConfigureNotebook} from "../../../../hooks/use-notebook-config";
import {MuiRFF, Yup} from '../../../../components/form';
import DateUtils from "../../../../../core/services/date-utils";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import {useAppSelector} from "../../../../redux";
import {useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../hooks/use-behavioural-analytics-tool";

const CustomerLoginLogsView = () => {
    const params = useParams();
    const [fetching, setFetching] = useState(false);
    const [loginLogs, setLoginLogs] = useState([]);
    const [filters, setFilters] = useState({
        startDate: DateUtils.serverNow().subtract(1, 'month').format(DateUtils.withoutOffsetFormat),
        endDate: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
    });
    const isMounted = useIsMounted();
    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('customer-login-logs-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        configureNotebookTags((tags) => tags.customerLoginLogs(defaultCustomer?.code));
        configureBehavioralTool((tags) => tags.customerLoginLogs(defaultCustomer?.code));
    }, [configureBehavioralTool, configureNotebookTags, defaultCustomer?.code]);

    /**
     * Memo version of total number of logins into the application
     * @type {string}
     */
    const totalNumberOfLogins = useMemo(() => {
        return 'N/A';
    }, []);

    /**
     * Memo version of number of logins in selected period which defaults to total number of logins
     * @type {number}
     */
    const sortedNumberOfLogins = useMemo(() => {
        return loginLogs.length;
    }, [loginLogs]);


    /**
     * Fetches the login logs of the current selected user from server:
     * - Calls the api regarding this action
     * - If the api call was successful, sets the [loginLogs] in the state to the received values
     * @return {Promise<void>}
     */
    const fetchLoginLogs = useCallback(async () => {
        setFetching(true);
        const forApi = {
            filters: {
                ...filters,
                customerId: Number(params.customerId),
                startDate: filters.startDate,
                endDate: filters.endDate,
            }
        };
        const response = await Api.searchLoginLogs(forApi);
        if (!isMounted())
            return;
        setFetching(false);
        if (response?.resultFlag) {
            setLoginLogs(response.data.resultList.map(e => ({
                date: e.date
            })));
        }
    }, [params.customerId, filters, isMounted]);

    /**
     * In initial render of the component, calls the function responsible for getting the login logs
     */
    useEffect(() => {
        void fetchLoginLogs()
    }, [fetchLoginLogs])

    return (
        <>
            <div className={'login-logs-view'}>
                <Paper className={'paper py-3 d-flex flex-column align-items-center justify-content-center'}>
                    <div className={'title'}>
                        <p>{`Login Report for ${defaultCustomer.name}`}</p>
                    </div>
                    <div className={'w-80'}>
                        <SearchSection
                            loading={fetching}
                            filters={filters}
                            setFilters={setFilters}
                        />
                    </div>
                    <ChartSection
                        dataset={loginLogs}
                        loading={fetching}
                    />
                    <Row className={'mt-5 mb-4'}>
                        <Col xs={6}>
                            <div className={'indicator'}>
                                <div className={'indicator-number'}>
                                    <p>{totalNumberOfLogins}</p>
                                </div>
                                <p className={'indicator-label'}>Total Number of Logins</p>
                            </div>
                        </Col>
                        <Col xs={6}>
                            <div className={'indicator'}>
                                <div className={'indicator-number'}>
                                    <p>{sortedNumberOfLogins}</p>
                                </div>
                                <p className={'indicator-label'}>Total Number of Logins in Selected Period</p>
                            </div>
                        </Col>
                    </Row>
                </Paper>
            </div>
        </>
    );
}


const formValues = {
    startDate: 'startDate',
    endDate: 'endDate',
}
const schema = Yup.object().shape({
    [formValues.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formValues.endDate), ValidateMessages.lessThan('End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    [formValues.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formValues.startDate), ValidateMessages.greaterThan('Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
});
const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const SearchSection = ({setFilters, loading, filters}) => {
    const initialValues = useMemo(() => ({
        [formValues.startDate]: filters.startDate ? DateUtils.serverToUTC(filters.startDate) : undefined,
        [formValues.endDate]: filters.endDate ? DateUtils.serverToUTC(filters.endDate) : undefined,
    }), [filters])

    /**
     * Submits the form and sets the filters to the values of the form
     * @param values
     */
    const onSubmit = useCallback((values) => {
        setFilters({
            startDate: values[formValues.startDate] ? DateUtils.UTCToServer(values[formValues.startDate]).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values[formValues.endDate] ? DateUtils.UTCToServer(values[formValues.endDate]).format(DateUtils.withoutOffsetFormat) : undefined,
        })
    }, [setFilters])

    return (
        <Col xs={12}>
            <Form
                className='w-100 mt-3'
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialValues}
                render={({values}) => {
                    return (
                        <div className={'d-flex flex-wrap w-100 align-items-center'}>
                            <Col xs={12} md={6} lg={5} className={'mb-4'}>
                                <MuiRFF.DatePicker
                                    name={formValues.startDate}
                                    label={"Start Date"}
                                    required={required[formValues.startDate]}
                                    maxDate={values[formValues.endDate]}
                                    disableFuture
                                />
                            </Col>
                            <Col xs={12} md={6} lg={5} className={'mb-4'}>
                                <MuiRFF.DatePicker
                                    name={formValues.endDate}
                                    label={"End Date"}
                                    required={required[formValues.endDate]}
                                    minDate={values[formValues.startDate]}
                                    disableFuture
                                />
                            </Col>
                            <Col xs={12} lg={2} className={'mb-4 d-flex'}>
                                <Button
                                    disabled={loading}
                                    className='px-8 py-3 m-auto'
                                    variant={'contained'}
                                    color={'primary'}
                                    type={'submit'}
                                >
                                    {loading ? 'Searching...' : 'Search'}
                                </Button>
                            </Col>
                        </div>
                    );
                }}
            />
        </Col>
    );
}

/**
 * Types of available visibilities for the chart
 */
const chartVisibilityTypes = {
    yearly: 'yearly',
    monthly: 'monthly',
    daily: 'daily',
    hourly: 'hourly'
}

/**
 * Chart Component shown int the login logs
 * @param dataset
 * @param loading
 * @param valueAggregator
 * @return {JSX.Element}
 * @constructor
 */
const ChartSection = ({dataset, loading}) => {
    const [visibility, setVisibility] = useState(chartVisibilityTypes.daily);

    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])
    const visibilities = useMemo(() => [
        {
            value: chartVisibilityTypes.yearly,
            name: "Yearly",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.monthly,
            name: "Monthly",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.daily,
            name: "Daily",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.hourly,
            name: "Hourly",
            disabled: loading
        }
    ], [loading])

    /**
     * Gets the string representation of the day of the week of a date
     * @param {DateUtilsTypes.Instance} date
     * @return {string}
     */
    const dayOfWeek = useCallback((date) => {
        const dow = date.day();
        switch (dow) {
            case 0:
            default:
                return "Sunday";
            case 1:
                return "Monday";
            case 2:
                return "Tuesday";
            case 3:
                return "Wednesday";
            case 4:
                return "Thursday";
            case 5:
                return "Friday";
            case 6:
                return "Saturday";

        }
    }, [])

    /**
     * Formats the hour returned from [hour] and returns the string representation
     * @param {number} hour
     */
    const formatHour = useCallback((hour) => hour < 10 ? `0${hour}:00` : `${hour}:00`, [])

    /**
     * Gets the data set ready for the chart by aggregating them based on the selected visibility type
     * @param data
     * @return {object}
     */
    const getDatasetData = useCallback((data = []) => {
        const aggregated = {};
        switch (visibility) {
            case  chartVisibilityTypes.hourly: {
                data
                    ?.sort((a, b) => DateUtils.serverToLocal(a.date).hour() - DateUtils.serverToLocal(b.date).hour())
                    ?.forEach(record => {
                        const dow = formatHour(DateUtils.serverToLocal(record.date).hour());
                        aggregated[dow] = !!aggregated[dow] ? aggregated[dow] += 1 : 1;
                    })
                break;
            }
            case  chartVisibilityTypes.daily: {
                data
                    ?.sort((a, b) => DateUtils.serverToLocal(a.date).day() - DateUtils.serverToLocal(b.date).day())
                    ?.forEach(record => {
                        const dow = dayOfWeek(DateUtils.serverToLocal(record.date));
                        aggregated[dow] = !!aggregated[dow] ? aggregated[dow] += 1 : 1;
                    })
                break;
            }
            case  chartVisibilityTypes.monthly: {
                data
                    ?.sort((a, b) => Utils.dateComparator(a.date, b.date))
                    ?.forEach(record => {
                        const date = DateUtils.serverToLocal(record.date).format('MMM YYYY');
                        aggregated[date] = !!aggregated[date] ? aggregated[date] += 1 : 1;
                    })
                break;
            }
            case  chartVisibilityTypes.yearly: {
                data
                    ?.sort((a, b) => Utils.dateComparator(a.date, b.date))
                    ?.forEach(record => {
                        const date = DateUtils.serverToLocal(record.date).format('YYYY');
                        aggregated[date] = !!aggregated[date] ? aggregated[date] += 1 : 1;
                    })
                break;
            }
            default:
                break;
        }
        return aggregated;
    }, [dayOfWeek, formatHour, visibility]);

    /**
     * Memo version of chart data passed to the
     * @type {{datasets: *[], labels: *[]}}
     */
    const chartData = useMemo(() => {
        const chartData = {
            datasets: [],
            labels: [],
        }
        const _data = getDatasetData(dataset);
        chartData.datasets.push({
            data: _data,
            label: 'Number of Logins',
            backgroundColor: chartColors[0],
        });
        chartData.labels = Object.keys(_data) ?? [];
        return chartData;
    }, [chartColors, dataset, getDatasetData]);

    return (
        <>
            <Col xs={12}
                 className={'my-4 transition mx-auto bar-chart'}>
                <BarChart height={window.innerHeight / 3} data={chartData}/>
            </Col>
            <Col xs={12} className={'d-flex justify-content-center mb-4'}>
                <ToggleButtons
                    size={'medium'}
                    value={visibility}
                    onChange={(_, v) => setVisibility(v)}
                    options={visibilities}
                />
            </Col>
        </>
    );

}

export default CustomerLoginLogsView;
